import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import FineUploaderS3 from 'fine-uploader-wrappers/s3';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
  Label,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModalTop } from '../../actions';
import { updateFilesOnFilesList } from '../../actions/Files';

import {
  APIconvertFile,
  APIcreateFile,
  APIcreateFileVersion,
  APIupdateFile,
} from '../../api/Files';

import ProcessingSpinner from '../ProcessingSpinner';

import { sendTrackingEvent } from '../../helpers';
import { getAPIUrl } from '../../helpers/App';
import { userCan, userCanSeeVersions } from '../../helpers/Auth';
import {
  fileToBase64, getFileExtension, getImageEditorSaveOriginalName,
} from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';

class ImageEditorSave extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      editedFile: null,
      meta: {},
      fileKey: null,
      uploading: false,
      error: null,
      saveType: null,
      name: '',
      copyComments: 'all',
    };

    this.fileInput = React.createRef();
    this.initUploader();

    this.uploadFile = this.uploadFile.bind(this);
    this.triggerInputFile = this.triggerInputFile.bind(this);
    this.handleChangeFileName = this.handleChangeFileName.bind(this);
    this.getFileUuid = this.getFileUuid.bind(this);
    this.handleCopyComments = this.handleCopyComments.bind(this);
  }

  componentDidMount() {
    const { intl, file } = this.props;

    this.setState({
      name: file.name,
    });

    this.uploader.on('submitted', (id) => {
      const { loggedInProfile } = this.props;
      this.setState({
        uploading: true,
        error: null,
      });
      this.uploader.methods.setParams({
        client: loggedInProfile.client.uuid,
      }, id);
    });

    this.uploader.on('complete', (id, name, response) => {
      if (!response.success) {
        return;
      }
      this.handleChangeImage(this.uploader.methods.getKey(id));
    });

    this.uploader.on('error', (id, name, error) => {
      this.setState({
        uploading: false,
        error,
      });
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({
          defaultMessage: '<strong>Something went wrong.</strong>{br}Please check for error messages.',
        }, {
          strong: (msg) => `<strong>${msg}</strong>`,
          br: '\n',
        }),
      });
    });
  }

  initUploader() {
    const { intl } = this.props;

    this.uploader = new FineUploaderS3({
      options: {
        debug: process.env.MIX_ENV !== 'production',
        multiple: false,
        request: {
          endpoint: `https://${process.env.MIX_AWS_S3_BUCKET_NAME}.s3.amazonaws.com`,
          accessKey: `${getSetting('upload_key') || process.env.MIX_AWS_ACCESS_KEY_ID}`,
        },
        objectProperties: {
          key: (id) => {
            const { loggedInProfile } = this.props;
            const ext = getFileExtension({ path: this.uploader.methods.getName(id) });
            const uuid = this.uploader.methods.getUuid(id);
            return `${loggedInProfile.client.uuid}/${uuid.substring(0, 3)}/${uuid}.${ext}`;
          },
        },
        validation: {
          image: {
            minHeight: 1,
            minWidth: 1,
          },
        },
        messages: {
          minHeightImageError: intl.formatMessage({ defaultMessage: 'Image is too small{br}(minimum size: 1x1 px)' }, {
            br: '<br />',
          }),
          minWidthImageError: intl.formatMessage({ defaultMessage: 'Image is too small{br}(minimum size: 1x1 px)' }, {
            br: '<br />',
          }),
        },
        signature: {
          endpoint: getAPIUrl('/upload'),
          version: 4,
          customHeaders: () => ({
            SPAuth: true,
            Authorization: `Bearer ${Cookies.get('sp_access_token')}`,
          }),
        },
        chunking: {
          enabled: true,
          concurrent: {
            enabled: true,
          },
        },
      },
    });
  }

  async downloadFile(target) {
    const {
      intl,
      getEditedFile,
      file,
      shouldForcePNG,
    } = this.props;
    const { name } = this.state;

    this.setState({
      saveType: 'download',
    });

    const dataURLToBlob = (dataURL) => {
      const [metadata, base64Data] = dataURL.split(',');

      const mimeMatch = metadata.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

      const binary = atob(base64Data);

      const array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        array[i] = binary.charCodeAt(i);
      }

      return new Blob([array], { type: mime });
    };

    const saveAs = (blob, filename) => {
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 1000);

      sendTrackingEvent('file-editor-edition-downloaded');
    };

    const getDownloadName = (editedFile) => {
      if (name.length > 0) {
        return name;
      }

      // Remove extension
      return editedFile.name.replace(/\.[^/.]+$/, '');
    };

    const needsFormatConversion = (targetFormat) => {
      const currentExtension = shouldForcePNG ? 'png' : file.extension;

      return currentExtension !== targetFormat.toLowerCase();
    };

    try {
      const { editedFile } = await getEditedFile();

      const needsConversion = needsFormatConversion(target);

      let finalBlob;
      let downloadFilename;

      if (needsConversion) {
        const editedFileBase64 = await fileToBase64(editedFile);

        const response = await APIconvertFile(file.uuid, {
          target,
          file_base64: editedFileBase64,
        });

        finalBlob = dataURLToBlob(response.file_base64);
        downloadFilename = `${getDownloadName(editedFile)}.${target}`;
      } else {
        finalBlob = editedFile;
        downloadFilename = name.length > 0 ? `${name}.${target}` : editedFile.name;
      }

      saveAs(finalBlob, downloadFilename);

      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'Your file has been downloaded' }),
      });
    } catch (error) {
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({ defaultMessage: 'Something went wrong. Please try again later.' }),
      });
    } finally {
      this.setState({
        saveType: null,
      });
    }
  }

  handleCopyComments(files, value) {
    this.setState({ copyComments: value }, () => {
      this.uploader.methods.addFiles(files);
    });
  }

  uploadFile(saveType) {
    const {
      file, getEditedFile, handleShowCommentsCopy, updateCommentsCount,
    } = this.props;

    getEditedFile()
      .then(({ editedFile, meta }) => {
        this.setState({
          editedFile,
          meta,
          saveType,
        }, () => {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(editedFile);
          const fileList = dataTransfer.files;

          if (saveType === 'version' && file.comments_count && file.comments_count.all > 0) {
            const modalParams = {
              handleCopyComments: this.handleCopyComments,
              commentsCount: file.comments_count,
              commentsCopied: file.comments_copied,
              file,
              files: fileList,
              updateCommentsCount,
            };
            handleShowCommentsCopy('create-version', modalParams);
          } else {
            this.uploader.methods.addFiles(fileList);
          }
        });
      })
      .catch(() => {
        this.setState({
          error: true,
        });
      });
  }

  triggerInputFile() {
    const { uploading } = this.state;
    if (!uploading) {
      this.fileInput.current.click();
    }
  }

  getFileUuid() {
    const { file } = this.props;
    if (file.parent) return file.parent.uuid;
    if (file.parent_uuid) return file.parent_uuid;
    return file.uuid;
  }

  handleChangeImage(imageUrl) {
    const { saveType } = this.state;
    this.setState({
      fileKey: imageUrl,
    }, () => {
      if (saveType === 'version') {
        this.handleCreateVersion();
      } else {
        this.handleCreateEdition();
      }
    });
  }

  handleCreateEdition() {
    const {
      fileKey,
      editedFile,
      name,
      meta,
    } = this.state;
    const {
      intl,
      file,
      fileVersion,
      afterCreateEdition,
      shouldForcePNG,
    } = this.props;

    const fileMeta = {
      bucket: process.env.MIX_AWS_S3_BUCKET_NAME,
      width: meta.width,
      height: meta.height,
      key: fileKey,
      original_name: getImageEditorSaveOriginalName(file, shouldForcePNG),
      type: editedFile.type,
      size: editedFile.size,
      name,
    };

    APIcreateFile(fileMeta).then((response) => {
      if (response.data) {
        const updateFileMeta = {
          uuid: response.data.uuid,
          parent_uuid: this.getFileUuid(),
          parent_version_id: fileVersion,
          copy_parent_meta: true,
        };

        APIupdateFile(updateFileMeta).then((updateResponse) => {
          const { data } = updateResponse;
          this.setState({
            saveType: null,
            uploading: false,
          }, () => {
            afterCreateEdition(data);
            addFlashMessage({
              type: 'success',
              text: intl.formatMessage({ defaultMessage: 'New file edition has been created' }),
            });

            sendTrackingEvent('file-editor-edition-created');
          });
        });
      }
    });
  }

  handleCreateVersion() {
    const {
      fileKey,
      editedFile,
      name,
      copyComments,
      meta,
    } = this.state;
    const {
      intl,
      file,
      handleUpdateFilesOnFilesList,
      afterCreateVersion,
      shouldForcePNG,
    } = this.props;

    const targetFileUuid = () => {
      if (file.parent_uuid) return file.parent_uuid;
      if (file.parent && file.parent.uuid) return file.parent.uuid;
      return file.uuid;
    };

    const fileMeta = {
      width: meta.width,
      height: meta.height,
      path: fileKey,
      original_name: getImageEditorSaveOriginalName(file, shouldForcePNG),
      type: editedFile.type,
      size: editedFile.size,
      name,
      copy_comments: copyComments,
    };

    APIcreateFileVersion(targetFileUuid(), fileMeta)
      .then((response) => {
        const updateFileMeta = {
          uuid: response.data.uuid,
          name,
        };
        APIupdateFile(updateFileMeta).then((updateResponse) => {
          if (updateResponse.data) {
            afterCreateVersion(updateResponse.data);
            handleUpdateFilesOnFilesList(updateResponse.data);
          }
          this.setState({
            saveType: null,
            uploading: false,
          });
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'New file version has been created' }),
          });

          sendTrackingEvent('file-editor-version-created');
        });
      });
  }

  handleChangeFileName(e) {
    const { value } = e.target;
    this.setState({
      name: value,
    });
  }

  render() {
    const {
      uploading,
      error,
      name,
      saveType,
    } = this.state;
    const {
      intl,
      file,
      handleShowDownloadModal,
    } = this.props;

    const fileExtensions = [
      {
        label: 'JPG',
        key: 'jpeg',
      },
      {
        label: 'PNG',
        key: 'png',
      },
      {
        label: 'WEBP',
        key: 'webp',
      },
    ];

    const userCanSaveVersion = userCanSeeVersions(file);
    const userCanSaveEdition = userCan('file.edit', file) && userCan('file.use_image_editor');

    return (
      <div className="image-editor-controls-row">
        <div className="image-editor-controls">
          <div className="image-editor-save-controls">
            <Label for="fileName" className="image-editor-label-column">
              <span>{intl.formatMessage({ defaultMessage: 'File name' })}</span>
              <Input
                id="fileName"
                placeholder={intl.formatMessage({ defaultMessage: 'File name' })}
                defaultValue={name}
                onChange={this.handleChangeFileName}
                className="input-no-margin"
              />
            </Label>
          </div>
          <div className="image-editor-save-controls image-editor-save-controls-buttons">
            <Button
              color="primary"
              onClick={() => {
                handleShowDownloadModal(
                  file,
                  fileExtensions,
                  (keys) => keys.map(k => this.downloadFile(k)),
                );
                sendTrackingEvent('file-editor-download-options-opened');
              }}
            >
              {intl.formatMessage({ defaultMessage: 'Download' })}
            </Button>
            {(userCanSaveEdition || userCanSaveVersion) && (
              <UncontrolledDropdown group>
                <DropdownToggle
                  caret
                  color="primary"
                  disabled={!!(uploading || saveType)}
                >
                  <ProcessingSpinner show={saveType === 'version' || saveType === 'edition'} />
                  {!saveType && intl.formatMessage({ defaultMessage: 'Save new' })}
                  {saveType === 'version' && intl.formatMessage({ defaultMessage: 'Saving a new version' })}
                  {saveType === 'edition' && intl.formatMessage({ defaultMessage: 'Saving a new edition' })}
                </DropdownToggle>
                <DropdownMenu>
                  {userCanSaveEdition && (
                    <DropdownItem onClick={() => {
                      this.uploadFile('edition');
                      sendTrackingEvent('file-editor-save-new-edition');
                    }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Save a new edition' })}
                    </DropdownItem>
                  )}
                  {userCanSaveVersion && (
                    <DropdownItem onClick={() => {
                      this.uploadFile('version');
                      sendTrackingEvent('file-editor-save-new-version');
                    }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Save a new version' })}
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
          <div className="image-editor-save-controls">
            {error && (
              <span className="image-editor-save-info image-editor-save-error">
                {intl.formatMessage({ defaultMessage: 'An error occured. Try again.' })}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  loggedInProfile: state.Auth.profile,
});

const mapDispatchToProps = dispatch => ({
  handleUpdateFilesOnFilesList: (data) => {
    dispatch(updateFilesOnFilesList([data]));
  },
  handleShowCommentsCopy: (mode, modalParams) => {
    dispatch(showModalTop('CommentsCopyModal', {
      className: 'modal-comments-copy',
      mode,
      ...modalParams,
    }));
  },
  handleShowDownloadModal: (file, fileExtensions, downloadCallback) => {
    dispatch(showModalTop('ImageEditorDownloadModal', {
      file,
      className: 'modal-download modal-image-editor-download',
      fileExtensions,
      downloadCallback,
    }));
  },
});

ImageEditorSave.propTypes = {
  intl: PropTypes.object.isRequired,
  getEditedFile: PropTypes.func.isRequired,
  handleUpdateFilesOnFilesList: PropTypes.func.isRequired,
  loggedInProfile: PropTypes.object.isRequired,
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  afterCreateVersion: PropTypes.func.isRequired,
  afterCreateEdition: PropTypes.func.isRequired,
  handleShowCommentsCopy: PropTypes.func.isRequired,
  handleShowDownloadModal: PropTypes.func.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
  shouldForcePNG: PropTypes.bool.isRequired,
};

ImageEditorSave.defaultProps = {
  fileVersion: null,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ImageEditorSave));
